import "./ActivityLogTimelineComponent.scss";

import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import React from 'react';
import AccordionComponent from "../accordion/accordionComponent";
import {timelineItemClasses} from "@mui/lab/TimelineItem";
import ChipComponent from "../chip/ChipComponent";
import moment from "moment";
import DetailsDataKeyValueComponent from "../details-data-key-value-component/detailsDataKeyValueComponent";
import commonService from "../../services/common.service";
import {CommonService} from "../../services";

interface ActivityLogTimelineComponentProps {
    logsData?: any;
}

const ignoreActivityFields = ['createdAt', 'updatedAt', 'reasonForEdit', 'patientId', 'id', 'insuranceId', 'isChecked', 'isDeleted', 'isInsuranceAvailable', 'loginUserId', 'labId', 'isSpecialPatient', 'isSelfRegistered', 'isSameName', 'isPasswordSet', 'isMobileNumberVerified']
const LogChanges = (props: any) => {
    const {logItem} = props;
    const featureType = logItem?.feature;
    const from = logItem?.data?.from;
    const to = logItem?.data?.to;
    if (featureType === "ICD Codes") {
        return <>
            <DetailsDataKeyValueComponent title={"From"}/>
            <ul className={'un-order-activity-list'}>
                {from?.map((item: any, index: number) => (
                    <React.Fragment key={index}>
                        <li>
                            <div>
                                <div>ICD Code: {item.icdCode}</div>
                                <div>Description: {item.description}</div>
                            </div>
                        </li>
                        {/* Only render the horizontal line if it's not the last item */}
                        {index < from.length - 1 && <div className={'horizontal-line'}></div>}
                    </React.Fragment>
                ))}
            </ul>
            <DetailsDataKeyValueComponent title={"To"}/>
            <ul className={'un-order-activity-list'}>
                {to?.map((item: any, index: number) => (
                    <React.Fragment key={index}>
                        <li>
                            <div>
                                <div>ICD Code: {item.icdCode}</div>
                                <div>Description: {item.description}</div>
                            </div>
                        </li>
                        {/* Only render the horizontal line if it's not the last item */}
                        {index < to.length - 1 && <div className={'horizontal-line'}></div>}
                    </React.Fragment>
                ))}
            </ul>
        </>
    } else if (featureType === "Medication") {
        const renderMedicationList = (medications: any[], title: string) => (
            <>
                <DetailsDataKeyValueComponent title={title}/>
                <ul className={'un-order-activity-list'}>
                    {medications?.map((item: any, index: number) => {
                        // Use the formatMedicationNames function for consistent formatting
                        const {
                            brandName = '',
                            genericName = '',
                            dosageForm = '',
                            dosage = '',
                            unit = ''
                        } = CommonService.formatMedicationNames(item);

                        return (
                            <React.Fragment key={index}>
                                <li>
                                    <div>
                                        <div>Brand Name: {brandName}</div>
                                        <div>Generic Name: {genericName}</div>
                                        <div>Dosage Form: {dosageForm}</div>
                                        <div>Dosage: {dosage}</div>
                                        <div>Unit: {unit}</div>
                                    </div>
                                </li>
                                {/* Only render the horizontal line if it's not the last item */}
                                {index < medications.length - 1 && <div className={'horizontal-line'}></div>}
                            </React.Fragment>
                        );
                    })}
                </ul>
            </>
        );

        return (
            <>
                {renderMedicationList(from, "From")}
                {renderMedicationList(to, "To")}
            </>
        );
    }

    return <>
        <DetailsDataKeyValueComponent
            title={"From"}> {from || '-'} </DetailsDataKeyValueComponent>
        <DetailsDataKeyValueComponent
            title={"To"}> {to || '-'} </DetailsDataKeyValueComponent>
    </>
}

const ActivityLogTimelineComponent = (props: ActivityLogTimelineComponentProps) => {
    const {logsData} = props;

    const getLogsStringWithArrows = (log: any) => {
        console.log(log);
        let str = log?.module && (log?.module + '$');
        str += log?.feature && (log?.feature + '$');
        str += log?.field && (log?.field + '$');
        str += log?.value ? (log?.value) : '';
        str.charAt(str?.length - 1) === '$' && (str = str.slice(0, -1));
        str = str.replace(/\$+/g, ' > ');
        return str;
    }

    const tConvert = (time: any) => {
        // Check correct time format and split into components
        time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];

        if (time.length > 1) { // If time format correct
            time = time.slice(1);  // Remove full string match value
            time[5] = +time[0] < 12 ? 'AM' : 'PM'; // Set AM/PM
            time[0] = +time[0] % 12 || 12; // Adjust hours
        }
        return time.join(''); // return adjusted time or original string
    }
    return (
        <div className={'activity-logs-timeline-component'}>
            <Timeline
                sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                    },
                }}>
                <>
                    {
                        logsData?.map((log: any, index: number) => {
                            return (
                                <TimelineItem key={index}>
                                    <TimelineSeparator>
                                        <TimelineConnector className={'initial-timeline-connector'}/>
                                        <TimelineDot color={"primary"}/>
                                        <TimelineConnector/>
                                    </TimelineSeparator>
                                    <TimelineContent>
                                        <div className={'log-item'}>
                                            <div className={'log-item-header'}>
                                                {log?.date}
                                            </div>
                                            <div className={'log-item-body'}>
                                                {log?.value?.map((logItem: any, index: number) => {
                                                    if (ignoreActivityFields.includes(logItem.field)) {
                                                        return <></>
                                                    }
                                                    if (logItem.action === 'edit' && !logItem?.data?.from) {
                                                        return <></>
                                                    }
                                                    return (
                                                        <div className={'mrg-bottom-20'} key={index}>
                                                            <AccordionComponent
                                                                key={index}
                                                                forActivityLog={true}
                                                                title={getLogsStringWithArrows(logItem)}
                                                                disableExpanding={logItem.action !== 'edit' ? true : false}
                                                                subTitle={logItem?.actionMessage || ''}
                                                                name={logItem?.userDetails ? (logItem?.userDetails?.firstName + ' ' + logItem?.userDetails?.lastName) : ''}
                                                                actions={<>
                                                                    <div className={'log-item-action'}>
                                                                        <ChipComponent
                                                                            label={logItem.action === 'add' ? 'Added' : logItem.action === 'edit' ? 'Modified' : commonService.capitalizeFirstLetterOfEachWord(logItem.action)}
                                                                            className={logItem.action}/>
                                                                    </div>
                                                                    <div className={'updated-date mrg-left-20'}>
                                                                        {tConvert(moment(logItem?.updatedAt).format('HH:mm'))}
                                                                    </div>
                                                                </>}
                                                            >
                                                                {
                                                                    logItem.action === 'edit' && <>
                                                                        <LogChanges logItem={logItem}/>
                                                                        <DetailsDataKeyValueComponent
                                                                            title={"Reason for edit"}/>
                                                                        <div
                                                                            className={'reason-for-edit'}>{logItem?.reasonForEdit || '-'}</div>
                                                                    </>
                                                                }
                                                            </AccordionComponent>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    </TimelineContent>
                                </TimelineItem>
                            )
                        })
                    }
                </>
            </Timeline>
        </div>
    );

};

export default ActivityLogTimelineComponent;
